import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { naira, formatDate, statusBadgeClass, statusLabel, daysBetween } from "../../lib/format";
import { KPI, Card, Loading, EmptyState } from "../../components/UI";
import { Home, FileSignature, CreditCard, Wrench, Calendar, Download, MessageSquare, ClipboardList } from "lucide-react";
import { useAuth } from "../../lib/auth";

export default function TenantDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [leases, setLeases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/tenants/me").catch(() => ({ data: null })),
      api.get("/leases"),
      api.get("/payments"),
      api.get("/maintenance"),
    ]).then(([p, l, pay, t]) => {
      setProfile(p.data); setLeases(l.data); setPayments(pay.data); setTickets(t.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading/>;
  if (!profile) {
    return (
      <EmptyState icon={ClipboardList} title="Complete your onboarding" description="Your tenant profile hasn't been set up yet." action={<Link to="/tenant/onboarding" className="btn-gold">Start Onboarding</Link>}/>
    );
  }

  const activeLease = leases.find(l => ["completed","manager_signed","lawyer_signed","tenant_signed","sent_to_tenant"].includes(l.lease_status)) || leases[0];
  const pendingPayments = payments.filter(p => p.payment_status === "pending");
  const totalPaid = payments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
  const openTickets = tickets.filter(t => ["open","assigned","in_progress"].includes(t.status));
  const today = new Date();
  const daysToExpiry = activeLease?.lease_end_date ? daysBetween(today, activeLease.lease_end_date) : null;

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9A227]">TENANT PORTAL</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A2E24]">Welcome, {user.full_name}</h1>
        <p className="text-[#5E7166] mt-1 text-sm">{profile.unit?.unit_name} • {profile.unit?.unit_type_name}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="My Unit" value={profile.unit?.unit_name || "—"} icon={Home} testid="kpi-my-unit"/>
        <KPI label="Monthly Rent" value={naira(profile.unit?.current_rent_amount)} icon={CreditCard} accent testid="kpi-my-rent"/>
        <KPI label="Days to Lease Expiry" value={daysToExpiry !== null ? `${daysToExpiry}` : "—"} icon={Calendar} testid="kpi-expiry"/>
        <KPI label="Total Paid" value={naira(totalPaid)} icon={CreditCard} testid="kpi-total-paid"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="My Lease" testid="my-lease" action={activeLease && <Link to="/tenant/lease" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">Full details →</Link>}>
          {activeLease ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#5E7166]">Status</span><span className={statusBadgeClass(activeLease.lease_status)}>{statusLabel(activeLease.lease_status)}</span></div>
              <div className="flex justify-between"><span className="text-[#5E7166]">Start</span><span className="font-semibold">{formatDate(activeLease.lease_start_date)}</span></div>
              <div className="flex justify-between"><span className="text-[#5E7166]">End</span><span className="font-semibold">{formatDate(activeLease.lease_end_date)}</span></div>
              <div className="flex justify-between"><span className="text-[#5E7166]">Total Payable</span><span className="font-serif font-bold text-[#C9A227]">{naira(activeLease.total_payable)}</span></div>
              <Link to="/tenant/lease" className="btn-emerald w-full justify-center" data-testid="view-my-lease"><FileSignature size={16}/> View / Sign Lease</Link>
            </div>
          ) : <div className="text-sm text-[#5E7166] py-4">No lease yet. Please contact your manager.</div>}
        </Card>

        <Card title="Payments" testid="my-payments" action={<Link to="/tenant/payments" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">All →</Link>}>
          {pendingPayments.length > 0 ? (
            <div className="space-y-3">
              {pendingPayments.slice(0,3).map(p => (
                <div key={p.id} className="border border-[#E5EBE8] rounded-lg p-3 flex items-center justify-between" data-testid={`pending-payment-${p.id}`}>
                  <div>
                    <div className="font-semibold text-sm">{statusLabel(p.payment_type)}</div>
                    <div className="text-xs text-[#5E7166]">{p.paystack_reference}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif font-bold text-[#C9A227]">{naira(p.amount)}</div>
                    <Link to={`/tenant/payments`} className="btn-gold text-xs mt-1">Upload Receipt</Link>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-[#5E7166] py-4">No pending payments.</div>}
        </Card>

        <Card title="Maintenance Tickets" testid="my-tickets" action={<Link to="/tenant/maintenance" className="text-xs text-[#0E5C3A] hover:text-[#C9A227]">All →</Link>}>
          {openTickets.length === 0 ? <div className="text-sm text-[#5E7166] py-4">No open tickets.</div> :
            <ul className="divide-y divide-[#E5EBE8]">
              {openTickets.slice(0,4).map(t => (
                <li key={t.id} className="py-2 flex items-center justify-between text-sm">
                  <span>{t.title}</span>
                  <span className={statusBadgeClass(t.status)}>{statusLabel(t.status)}</span>
                </li>
              ))}
            </ul>}
          <Link to="/tenant/maintenance" className="btn-outline-emerald w-full justify-center mt-3"><Wrench size={14}/> Submit New Request</Link>
        </Card>

        <Card title="Quick Actions">
          <div className="grid grid-cols-2 gap-2">
            <Link to="/tenant/feedback" className="btn-outline-emerald justify-center"><MessageSquare size={14}/> Feedback</Link>
            <Link to="/tenant/onboarding" className="btn-outline-emerald justify-center"><ClipboardList size={14}/> Profile</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
